import { useEffect, useState, useSyncExternalStore } from 'react';
import { formatBytes, formatPercent } from '../core/format';
import { diag } from '../core/diag';
import { redactText } from '../core/url';
import { engine, useApp } from '../state/store';
import { installPromptAvailable, onInstallChange, promptInstall } from '../pwa/register';
import { useSession } from './hooks/useSession';
import { Modal } from './parts/Modal';
import { MODE_COPY } from './state-copy';

export function Panels() {
  const panel = useApp((s) => s.panel);
  const setPanel = useApp((s) => s.setPanel);
  const close = () => setPanel(null);
  return (
    <>
      <DetailsPanel open={panel === 'details'} onClose={close} />
      <SettingsPanel open={panel === 'settings'} onClose={close} />
      <CorsPanel open={panel === 'cors'} onClose={close} />
      <HelpPanel open={panel === 'help'} onClose={close} />
      <InstallPanel open={panel === 'install'} onClose={close} />
      <LinkNeededDialog />
      <ConfirmRemoveDialog />
      <ConfirmRestartDialog />
    </>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="kv">
      <dt>{k}</dt>
      <dd className="mono">{v}</dd>
    </div>
  );
}

function DetailsPanel({ open, onClose }: { open: boolean; onClose(): void }) {
  const id = useApp((s) => s.activeId);
  const { record, snap, total, saved, fraction, transferred, overhead } = useSession(id);
  const toast = useApp((s) => s.toast);
  const [est, setEst] = useState<{
    usage: number | null;
    quota: number | null;
    persisted: boolean | null;
  } | null>(null);
  const [events, setEvents] = useState(() => [...diag.entries()]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    void engine.storageEstimate().then((e) => alive && setEst(e));
    setEvents([...diag.entries()]);
    const off = diag.subscribe(() => setEvents([...diag.entries()]));
    return () => {
      alive = false;
      off();
    };
  }, [open]);

  if (!record)
    return (
      <Modal open={open} onClose={onClose} title="Details">
        <p>Open a file to see its details.</p>
      </Modal>
    );

  const report = {
    file: record.name,
    host: record.host,
    path: redactText(record.safeUrl),
    kind: record.kind,
    container: record.container,
    contentType: record.contentType,
    state: record.state,
    totalBytes: total,
    savedBytes: saved,
    receivedBytes: transferred,
    resentBytes: overhead,
    requests: snap?.counters.requests ?? 0,
    failedRequests: snap?.counters.failedRequests ?? 0,
    rangeSupport: snap?.rangeProvenance ?? 'unknown',
    exposedHeaders: snap?.exposedHeaders ?? null,
    validators: snap?.identity
      ? { etag: snap.identity.etag, lastModified: snap.identity.lastModified }
      : null,
    savedPieces: snap?.readable.length ?? 0,
    error: record.lastError ? { code: record.lastError.code } : null,
    storage: est,
    recentEvents: events
      .slice(-30)
      .map((e) => ({
        t: new Date(e.t).toISOString(),
        level: e.level,
        scope: e.scope,
        message: e.message,
      })),
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title="Details"
      footer={
        <button
          type="button"
          className="btn"
          onClick={() => {
            void navigator.clipboard?.writeText(JSON.stringify(report, null, 2)).then(
              () =>
                toast({
                  kind: 'success',
                  title: 'Report copied',
                  body: 'Link parameters are left out.',
                }),
              () =>
                toast({
                  kind: 'error',
                  title: 'Could not copy',
                  body: 'Select the text and copy it by hand.',
                }),
            );
          }}
        >
          Copy redacted report
        </button>
      }
    >
      <p className="modal__lede">
        For technical users. Nothing here includes link parameters or tokens.
      </p>
      <dl className="kvs">
        <Row k="File" v={record.name} />
        <Row k="Source" v={record.safeUrl} />
        {snap?.finalHost && <Row k="Served from" v={snap.finalHost} />}
        <Row
          k="Detected type"
          v={`${record.kind}${record.container ? ` (${record.container})` : ''}`}
        />
        <Row k="Declared type" v={record.contentType ?? 'not reported'} />
        <Row k="Size" v={total === null ? 'not reported' : formatBytes(total)} />
        <Row k="Saved" v={`${formatBytes(saved)} · ${formatPercent(fraction)}`} />
        <Row k="Received" v={`${formatBytes(transferred)} (${formatBytes(overhead)} re-sent)`} />
        <Row
          k="Requests"
          v={`${snap?.counters.requests ?? 0} made, ${snap?.counters.failedRequests ?? 0} failed`}
        />
        <Row k="Saved pieces" v={snap?.readable.length ?? 0} />
        <Row k="Jumping ahead" v={describeRange(snap?.rangeProvenance)} />
        <Row k="Mode" v={MODE_COPY[record.mode].name} />
        <Row k="Headers the host exposes" v={snap ? exposedList(snap.exposedHeaders) : '—'} />
        <Row k="ETag" v={snap?.identity?.etag ?? 'not exposed'} />
        <Row k="Last modified" v={snap?.identity?.lastModified ?? 'not exposed'} />
        {est?.quota != null && (
          <Row
            k="Device storage"
            v={`${formatBytes(est.usage)} used of about ${formatBytes(est.quota)}${est.persisted ? ' · kept' : ''}`}
          />
        )}
      </dl>
      {events.length > 0 && (
        <details className="events">
          <summary>Recent events ({events.length})</summary>
          <ul className="events__list mono">
            {events
              .slice(-12)
              .reverse()
              .map((e, i) => (
                <li key={i} data-level={e.level}>
                  <span>{new Date(e.t).toLocaleTimeString()}</span> <strong>{e.scope}</strong>{' '}
                  {e.message}
                </li>
              ))}
          </ul>
        </details>
      )}
      <p className="modal__note">
        Received counts response payload bytes only, including retries. Saved counts unique bytes
        written to this device. Headers and connection overhead are not included.
      </p>
    </Modal>
  );
}

function describeRange(p: string | undefined): string {
  switch (p) {
    case 'proven':
      return 'Yes. The host returned correct partial responses.';
    case 'ignored':
      return 'No. The host sends the whole file in order.';
    case 'advertised-unproven':
      return 'Not proven. Only forward downloading is used.';
    default:
      return 'Not tested yet';
  }
}

function exposedList(e: {
  contentRange: boolean;
  etag: boolean;
  lastModified: boolean;
  contentLength: boolean;
}): string {
  const names = [
    e.contentLength && 'Content-Length',
    e.contentRange && 'Content-Range',
    e.etag && 'ETag',
    e.lastModified && 'Last-Modified',
  ].filter(Boolean);
  return names.length ? names.join(', ') : 'none seen yet';
}

function SettingsPanel({ open, onClose }: { open: boolean; onClose(): void }) {
  const settings = useApp((s) => s.settings);
  const update = useApp((s) => s.updateSettings);
  const toast = useApp((s) => s.toast);
  const records = useApp((s) => s.records);
  const remove = useApp((s) => s.remove);
  const [persisted, setPersisted] = useState<boolean | null>(null);
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setArmed(false);
    let alive = true;
    void engine.storageEstimate().then((e) => alive && setPersisted(e.persisted));
    return () => {
      alive = false;
    };
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} title="Settings">
      <div className="setting">
        <h3 className="setting__title">Default download mode</h3>
        <div className="seg seg--wide" role="radiogroup" aria-label="Default download mode">
          {(Object.keys(MODE_COPY) as (keyof typeof MODE_COPY)[]).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={settings.mode === m}
              className="seg__btn"
              onClick={() => update({ mode: m })}
            >
              {MODE_COPY[m].name}
            </button>
          ))}
        </div>
        <p className="setting__help">{MODE_COPY[settings.mode].help}</p>
      </div>

      <div className="setting">
        <label className="switch">
          <input
            type="checkbox"
            checked={settings.rememberSignedLinks}
            onChange={(e) => update({ rememberSignedLinks: e.target.checked })}
          />
          <span className="switch__track" aria-hidden="true" />
          <span className="switch__text">Remember links that contain parameters</span>
        </label>
        <p className="setting__help">
          Off by default. Links with a query string often carry a temporary key, so OneFlow keeps
          only the address without it and asks you to paste the link again to resume. Links without
          parameters are always remembered.
        </p>
      </div>

      <div className="setting">
        <h3 className="setting__title">Motion</h3>
        <div className="seg" role="radiogroup" aria-label="Motion">
          {(
            [
              ['system', 'Follow system'],
              ['reduce', 'Reduce'],
              ['full', 'Full'],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={settings.motion === v}
              className="seg__btn"
              onClick={() => update({ motion: v })}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="setting">
        <h3 className="setting__title">Keep my files</h3>
        <p className="setting__help">
          Browsers can clear site storage when a device is short on space. Asking to keep it lowers
          that risk but never removes it.
        </p>
        <button
          type="button"
          className="btn"
          disabled={persisted === true}
          onClick={async () => {
            const ok = await engine.requestPersistence();
            setPersisted(ok);
            toast({
              kind: ok ? 'success' : 'info',
              title: ok ? 'Storage will be kept' : 'The browser declined',
              body: ok
                ? undefined
                : 'It may allow this after you use OneFlow more, or when it is installed.',
            });
          }}
        >
          {persisted === true ? 'Storage is kept' : 'Ask the browser to keep my files'}
        </button>
      </div>

      <div className="setting">
        <h3 className="setting__title">Delete everything</h3>
        <p className="setting__help">
          Removes every saved file and its record from this device. This cannot be undone.
        </p>
        <button
          type="button"
          className="btn btn--danger"
          disabled={Object.keys(records).length === 0}
          onClick={async () => {
            if (!armed) return setArmed(true);
            for (const id of Object.keys(records)) await remove(id);
            setArmed(false);
            toast({ kind: 'success', title: 'All saved files deleted' });
          }}
        >
          {armed
            ? `Delete ${Object.keys(records).length} saved ${Object.keys(records).length === 1 ? 'file' : 'files'} now`
            : 'Delete all saved files'}
        </button>
      </div>
    </Modal>
  );
}

function CorsPanel({ open, onClose }: { open: boolean; onClose(): void }) {
  return (
    <Modal open={open} onClose={onClose} wide title="What counts as a direct link">
      <p>
        OneFlow needs a link that points straight at a file, such as{' '}
        <code>https://host.example/films/clip.mp4</code>, and a host that allows other websites to
        read it.
      </p>
      <h3 className="modal__h3">It will not work with</h3>
      <ul className="plain-list">
        <li>
          Pages on video sites. OneFlow does not extract media from streaming services or remove
          protection.
        </li>
        <li>Links that need a login or a cookie.</li>
        <li>
          Files from hosts that block cross-origin reads (CORS). Browsers enforce this and OneFlow
          does not work around it. It never uses a proxy.
        </li>
      </ul>
      <h3 className="modal__h3">If you run the host</h3>
      <p>
        Send these headers with the file. The second line is optional, but it lets OneFlow verify
        partial responses and detect a changed file.
      </p>
      <pre className="code code--snippet">{`Access-Control-Allow-Origin: *
Access-Control-Expose-Headers: Content-Range, ETag
Accept-Ranges: bytes`}</pre>
      <p>
        Without <code>Accept-Ranges</code> support, playback still works but only in order, and
        jumping ahead waits for the download.
      </p>
    </Modal>
  );
}

const SHORTCUTS: [string, string][] = [
  ['/', 'Focus the link field'],
  ['Space or K', 'Play or pause'],
  ['← →', 'Back or forward 5 seconds'],
  ['J  L', 'Back or forward 10 seconds'],
  ['M', 'Mute'],
  ['F', 'Focus mode'],
  ['Q', 'Show or hide the file list'],
  ['Esc', 'Leave focus mode or close a panel'],
  ['?', 'Show this list'],
];

function HelpPanel({ open, onClose }: { open: boolean; onClose(): void }) {
  return (
    <Modal open={open} onClose={onClose} title="Keyboard shortcuts">
      <dl className="shortcuts">
        {SHORTCUTS.map(([k, d]) => (
          <div key={k} className="shortcuts__row">
            <dt>
              <kbd>{k}</kbd>
            </dt>
            <dd>{d}</dd>
          </div>
        ))}
      </dl>
      <p className="modal__note">Shortcuts never fire while you type in a field.</p>
    </Modal>
  );
}

function InstallPanel({ open, onClose }: { open: boolean; onClose(): void }) {
  const can = useSyncExternalStore(onInstallChange, installPromptAvailable, () => false);
  const c = engine.caps;
  return (
    <Modal open={open} onClose={onClose} title="Install OneFlow">
      <p>
        Installing adds OneFlow to your home screen or app list and lets it open offline. Files you
        finished stay available without a connection.
      </p>
      {can && (
        <p>
          <button
            type="button"
            className="btn btn--accent"
            onClick={() => void promptInstall().then(onClose)}
          >
            Install now
          </button>
        </p>
      )}
      {!can && c.ios && <p>On iPhone or iPad: tap Share, then Add to Home Screen.</p>}
      {!can && !c.ios && (
        <p>
          In Chrome or Edge, use the install icon in the address bar. Firefox on desktop does not
          install web apps.
        </p>
      )}
      <p className="modal__note">
        An installed app can still be suspended when you leave it. Downloads may pause then and
        resume when you return. OneFlow never claims otherwise.
      </p>
    </Modal>
  );
}

function LinkNeededDialog() {
  const id = useApp((s) => s.linkNeeded);
  const setLinkNeeded = useApp((s) => s.setLinkNeeded);
  const resume = useApp((s) => s.resume);
  const { record } = useSession(id);
  const [v, setV] = useState('');
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (id) {
      setV('');
      setErr(null);
    }
  }, [id]);
  return (
    <Modal
      open={id !== null}
      onClose={() => setLinkNeeded(null)}
      title="Paste the link again"
      footer={
        <>
          <button type="button" className="btn" onClick={() => setLinkNeeded(null)}>
            Cancel
          </button>
          <button type="submit" form="link-again" className="btn btn--accent">
            Continue download
          </button>
        </>
      }
    >
      <form
        id="link-again"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!id) return;
          try {
            await resume(id, v);
          } catch {
            setErr('That link did not work.');
          }
        }}
      >
        <p>
          {record ? (
            <>
              OneFlow kept <strong>{record.name}</strong> but not its link, because links can carry
              temporary keys.
            </>
          ) : null}{' '}
          Paste the link to the same file and the download continues from the saved part.
        </p>
        <label className="source-form__label" htmlFor="link-again-input">
          Direct file link
        </label>
        <input
          id="link-again-input"
          className="field__input field__input--solo mono"
          value={v}
          onChange={(e) => setV(e.target.value)}
          spellCheck={false}
          autoComplete="off"
          placeholder="https://"
        />
        {err && (
          <p className="source-form__error" role="alert">
            {err}
          </p>
        )}
      </form>
    </Modal>
  );
}

function ConfirmRemoveDialog() {
  const id = useApp((s) => s.confirmRemove);
  const set = useApp((s) => s.setConfirmRemove);
  const remove = useApp((s) => s.remove);
  const { record, saved } = useSession(id);
  return (
    <Modal
      open={id !== null}
      onClose={() => set(null)}
      title="Delete this file?"
      footer={
        <>
          <button type="button" className="btn" onClick={() => set(null)}>
            Keep it
          </button>
          <button
            type="button"
            className="btn btn--danger-solid"
            onClick={() => id && void remove(id)}
          >
            Delete
          </button>
        </>
      }
    >
      <p>
        <strong>{record?.name}</strong> and the {formatBytes(saved)} saved on this device will be
        removed. Any download in progress stops. This cannot be undone.
      </p>
    </Modal>
  );
}

function ConfirmRestartDialog() {
  const id = useApp((s) => s.confirmRestart);
  const set = useApp((s) => s.setConfirmRestart);
  const restart = useApp((s) => s.restartFresh);
  const { record, saved } = useSession(id);
  return (
    <Modal
      open={id !== null}
      onClose={() => set(null)}
      title="Start over?"
      footer={
        <>
          <button type="button" className="btn" onClick={() => set(null)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--danger-solid"
            onClick={() => id && void restart(id)}
          >
            Discard and start over
          </button>
        </>
      }
    >
      <p>
        {record?.lastError?.code === 'SOURCE_CHANGED'
          ? 'The file at this link changed, so the saved part no longer matches.'
          : 'The saved data for this file cannot be trusted.'}{' '}
        Starting over deletes the {formatBytes(saved)} saved so far and downloads the file again.
      </p>
    </Modal>
  );
}
